<template>
  <div class="row justify-content-center social-form-container py-4">
    <div class="col-xl-9 col-lg-10">
      <div class="card shadow-lg border-0 modern-card overflow-hidden">
        <!-- Encabezado de diseño premium -->
        <div class="card-header modern-card-header d-flex align-items-center justify-content-between py-3">
          <h3 class="card-title mb-0 font-weight-bold text-dark d-flex align-items-center">
            <span class="brand-icon-wrapper mr-2">
              <i class="fas fa-share-alt text-white"></i>
            </span>
            {{ isEdit ? 'Editar Publicación' : 'Nueva Publicación en Redes Sociales' }}
          </h3>
          <span class="badge" :class="formData.tipo_post === 'reel_video' ? 'badge-danger' : 'badge-primary'">
            {{ formData.tipo_post === 'reel_video' ? 'Vídeo / Reel' : 'Post Clásico' }}
          </span>
        </div>

        <div class="card-body p-4">
          <!-- Alerta de Errores del Servidor (Laravel) -->
          <transition name="fade-slide">
            <div v-if="serverErrors.length" class="alert alert-danger shadow-sm mb-4 border-0 error-alert">
              <h5 class="font-weight-bold text-sm mb-2 d-flex align-items-center">
                <i class="fas fa-exclamation-triangle mr-2"></i> Por favor, corrige los siguientes errores:
              </h5>
              <ul class="mb-0 pl-3 small">
                <li v-for="(err, index) in serverErrors" :key="index">{{ err }}</li>
              </ul>
            </div>
          </transition>

          <FormKit
            type="form"
            v-model="formData"
            @submit="handleSubmit"
            :actions="false"
          >
            <!-- SECCIÓN 1: DESTINOS Y PLANTILLA DE DISEÑO -->
            <div class="mb-5">
              <label class="form-section-title d-block mb-3">
                <i class="fas fa-bullhorn mr-2 text-danger"></i> 1. Configuración de Publicación
              </label>

              <div class="row">
                <!-- Plataformas de destino -->
                <div class="col-md-6 mb-4 mb-md-0">
                  <label class="d-block font-weight-bold text-muted small text-uppercase tracking-wider mb-3">
                    Redes Sociales de Destino <span class="text-danger">*</span>
                  </label>
                  <div class="d-flex flex-wrap gap-2">
                    <button 
                      type="button" 
                      class="platform-btn fb" 
                      :class="{ active: formData.facebook }" 
                      @click="formData.facebook = !formData.facebook"
                    >
                      <span class="platform-icon"><i class="fab fa-facebook-f"></i></span>
                      <span class="platform-name">Facebook</span>
                    </button>
                    <button 
                      type="button" 
                      class="platform-btn ig" 
                      :class="{ active: formData.instagram }" 
                      @click="formData.instagram = !formData.instagram"
                    >
                      <span class="platform-icon"><i class="fab fa-instagram"></i></span>
                      <span class="platform-name">Instagram</span>
                    </button>
                    <button 
                      type="button" 
                      class="platform-btn tw" 
                      :class="{ active: formData.twitter }" 
                      @click="formData.twitter = !formData.twitter"
                    >
                      <span class="platform-icon"><i class="fab fa-twitter"></i></span>
                      <span class="platform-name">Twitter / X</span>
                    </button>
                  </div>
                </div>

                <!-- Seleccionar Plantilla de Diseño -->
                <div class="col-md-6">
                  <div class="form-group">
                    <label for="select-plantilla" class="form-label font-weight-bold text-muted small text-uppercase tracking-wider mb-2">
                      <i class="fas fa-magic mr-1 text-secondary"></i> Seleccionar Plantilla de Diseño
                    </label>
                    <select 
                      name="plantilla" 
                      id="select-plantilla" 
                      class="form-control modern-input" 
                      v-model="formData.plantilla"
                    >
                      <option value="">Seleccione una plantilla...</option>
                      <option v-for="i in 25" :key="i" :value="i">
                        Plantilla {{ i }}
                      </option>
                    </select>

                    <!-- Miniatura de Plantilla directamente debajo -->
                    <transition name="fade-slide">
                      <div v-if="formData.plantilla && rfTemplates && rfTemplates[formData.plantilla]" class="template-preview-box text-center mt-3 p-3 rounded border bg-light shadow-xs position-relative">
                        <img :src="rfTemplates[formData.plantilla]" class="img-fluid rounded shadow-sm hover-zoom" style="max-height: 140px;" alt="Preview Plantilla">
                        <div class="mt-2 text-xs text-secondary font-weight-bold">Miniatura de la Plantilla {{ formData.plantilla }}</div>
                      </div>
                      <div v-else-if="formData.plantilla" class="template-preview-box text-center mt-3 p-3 rounded border bg-light text-muted text-xs">
                        <i class="fas fa-info-circle mr-1"></i> Sin miniatura configurada en el servidor.
                      </div>
                    </transition>
                  </div>
                </div>
              </div>
            </div>

            <hr class="my-4 border-light">

            <!-- SECCIÓN 2: FORMATO DE PUBLICACIÓN -->
            <div class="mb-5">
              <label class="form-section-title d-block mb-3">
                <i class="fas fa-film mr-2 text-danger"></i> 2. Formato de Contenido
              </label>

              <!-- Tarjetas Visuales de Formato -->
              <div class="row">
                <div class="col-md-6 mb-3 mb-md-0">
                  <div 
                    class="format-card" 
                    :class="{ active: formData.tipo_post === 'post' }"
                    @click="setTipoPost('post')"
                  >
                    <div class="format-icon-box">
                      <i class="far fa-images"></i>
                    </div>
                    <div class="format-details">
                      <span class="format-name">Post Clásico</span>
                      <span class="format-desc">Diseño con imágenes estáticas</span>
                    </div>
                    <div class="check-indicator">
                      <i class="fas fa-check-circle"></i>
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div 
                    class="format-card" 
                    :class="{ active: formData.tipo_post === 'reel_video' }"
                    @click="setTipoPost('reel_video')"
                  >
                    <div class="format-icon-box">
                      <i class="fas fa-film"></i>
                    </div>
                    <div class="format-details">
                      <span class="format-name">Vídeo / Reel</span>
                      <span class="format-desc">Vídeos verticales y Reels</span>
                    </div>
                    <div class="check-indicator">
                      <i class="fas fa-check-circle"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <hr class="my-4 border-light">

            <!-- SECCIÓN 3: CONTENIDO DEL MENSAJE -->
            <div class="mb-5">
              <label class="form-section-title d-block mb-3">
                <i class="fas fa-edit mr-2 text-danger"></i> 3. Contenido del Mensaje
              </label>

              <!-- Categoría / Sección con select nativo de Bootstrap -->
              <div class="form-group mb-4">
                <label for="seccion" class="form-label font-weight-bold text-muted small text-uppercase tracking-wider mb-2">
                  Categoría / Sección <span class="text-danger">*</span>
                </label>
                <select class="form-control modern-input" id="seccion" v-model="formData.seccion" required>
                  <option value="" disabled>Seleccione una categoría...</option>
                  <option v-for="opt in sections" :key="opt" :value="opt">{{ opt }}</option>
                </select>
              </div>

              <div class="row">
                <div class="col-md-6 mb-3">
                  <FormKit
                    type="text"
                    label="Cabecera Principal"
                    name="cabecera"
                    v-model="formData.cabecera"
                    placeholder="Ej: ÚLTIMA HORA"
                  />
                </div>
                <div class="col-md-6 mb-3">
                  <FormKit
                    type="text"
                    label="Cabecera Secundaria"
                    name="cabecera2"
                    v-model="formData.cabecera2"
                    placeholder="Ej: Sector Enseñanza"
                  />
                </div>
              </div>

              <div class="mb-3">
                <FormKit
                  type="text"
                  label="Título de la Publicación"
                  name="titulo"
                  v-model="formData.titulo"
                  placeholder="Escribe el título que irá en la imagen..."
                  validation="required|length:3,255"
                />
              </div>

              <div class="mb-3">
                <FormKit
                  type="text"
                  label="Subtítulo"
                  name="subtitulo"
                  v-model="formData.subtitulo"
                  placeholder="Subtítulo secundario (opcional)"
                />
              </div>

              <div class="mb-3">
                <FormKit
                  type="textarea"
                  label="Texto del Post (Descripción)"
                  name="texto"
                  v-model="formData.texto"
                  rows="6"
                  placeholder="Contenido descriptivo de la publicación..."
                  help="Soporta texto plano con saltos de línea."
                />
              </div>

              <!-- Pie de post (Hashtags) - Solo visible para Reels/Vídeos en la primera pantalla -->
              <transition name="fade-slide">
                <div v-if="formData.tipo_post === 'reel_video'" class="mb-3">
                  <FormKit
                    type="text"
                    label="Texto a añadir para descripción en redes sociales (hashtags)"
                    name="pie"
                    v-model="formData.pie"
                    placeholder="Ej: #FEUSO #Asturias #Educacion"
                  />
                </div>
              </transition>
            </div>

            <hr class="my-4 border-light">

            <!-- SECCIÓN 4: MULTIMEDIA Y PROGRAMACIÓN -->
            <div class="mb-4">
              <label class="form-section-title d-block mb-3">
                <i class="fas fa-camera mr-2 text-danger"></i> 4. Multimedia y Programación
              </label>

              <!-- Campos específicos para Vídeos (Solo si es Vídeo/Reel) -->
              <transition name="fade-slide">
                <div v-if="formData.tipo_post === 'reel_video'" class="mb-4 p-3 rounded bg-light border border-light">
                  <div class="row">
                    <div class="col-md-6 mb-3 mb-md-0">
                      <FormKit
                        type="url"
                        label="URL del Vídeo Principal"
                        name="video_url_1"
                        v-model="formData.video_url_1"
                        placeholder="https://..."
                        help="Google Drive, AWS S3, etc."
                      />
                    </div>
                    <div class="col-md-6">
                      <FormKit
                        type="url"
                        label="URL del Vídeo Secundario (Opcional)"
                        name="video_url_2"
                        v-model="formData.video_url_2"
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                </div>
              </transition>

              <!-- Campos de Imagen -->
              <div class="row mb-3">
                <div class="col-md-6 mb-3">
                  <label class="form-label font-weight-bold small text-muted text-uppercase mb-2">
                    {{ formData.tipo_post === 'reel_video' ? 'Portada del Reel (Cover)' : 'Imagen Principal' }}
                  </label>
                  
                  <!-- Imagen ya existente en modo edición -->
                  <div v-if="initialImages.image1 && !deleteImages.image1" class="existing-img-card mb-2 p-2 rounded d-flex align-items-center justify-content-between shadow-sm">
                    <img :src="initialImages.image1" class="rounded object-fit-cover" style="width: 50px; height: 50px;">
                    <span class="text-xs text-muted text-truncate ml-2 mr-auto">Imagen cargada</span>
                    <button type="button" class="btn btn-danger btn-xs btn-delete-existing" @click="markImageDeleted('image1')">
                      <i class="fas fa-trash-alt mr-1"></i> Eliminar
                    </button>
                  </div>

                  <input type="file" class="form-control-file premium-file-input mb-2" @change="onFileChange($event, 'image1')">
                  
                  <div v-if="previews.image1" class="preview-box-premium rounded shadow-sm border p-1 position-relative">
                    <img :src="previews.image1" class="img-fluid rounded" style="max-height: 120px;">
                    <button type="button" class="btn btn-dark btn-xs btn-remove-preview" @click="clearPreview('image1')">
                      <i class="fas fa-times"></i>
                    </button>
                  </div>
                </div>

                <div class="col-md-6 mb-3">
                  <label class="form-label font-weight-bold small text-muted text-uppercase mb-2">Imagen Secundaria</label>

                  <!-- Imagen ya existente en modo edición -->
                  <div v-if="initialImages.image2 && !deleteImages.image2" class="existing-img-card mb-2 p-2 rounded d-flex align-items-center justify-content-between shadow-sm">
                    <img :src="initialImages.image2" class="rounded object-fit-cover" style="width: 50px; height: 50px;">
                    <span class="text-xs text-muted text-truncate ml-2 mr-auto">Imagen cargada</span>
                    <button type="button" class="btn btn-danger btn-xs btn-delete-existing" @click="markImageDeleted('image2')">
                      <i class="fas fa-trash-alt mr-1"></i> Eliminar
                    </button>
                  </div>

                  <input type="file" class="form-control-file premium-file-input mb-2" @change="onFileChange($event, 'image2')">
                  
                  <div v-if="previews.image2" class="preview-box-premium rounded shadow-sm border p-1 position-relative">
                    <img :src="previews.image2" class="img-fluid rounded" style="max-height: 120px;">
                    <button type="button" class="btn btn-dark btn-xs btn-remove-preview" @click="clearPreview('image2')">
                      <i class="fas fa-times"></i>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Fecha y Hora de Publicación (Solo para Reels en la primera pantalla) -->
              <transition name="fade-slide">
                <div v-if="formData.tipo_post === 'reel_video'" class="row">
                  <div class="col-md-6">
                    <FormKit
                      type="datetime-local"
                      label="Programar Publicación para:"
                      name="fechayhorapublicacion"
                      v-model="formData.fechayhorapublicacion"
                      validation="required"
                      help="Elige la fecha y hora de emisión automática"
                    />
                  </div>
                </div>
              </transition>
            </div>

            <!-- BOTONES DE ACCIÓN FLOTANTES / PIE DE FORMULARIO -->
            <div class="d-flex justify-content-between align-items-center mt-5 pt-3 border-top border-light">
              <a :href="backUrl" class="btn btn-light btn-lg px-4 border shadow-xs text-secondary btn-cancel font-weight-bold">
                <i class="fas fa-times mr-2"></i> Cancelar
              </a>
              <button 
                type="submit" 
                class="btn btn-brand-red btn-lg px-5 shadow font-weight-bold" 
                :disabled="isSubmitting"
              >
                <span v-if="isSubmitting">
                  <i class="fas fa-spinner fa-spin mr-2"></i> Procesando...
                </span>
                <span v-else>
                  <i class="fas fa-arrow-right mr-2"></i>
                  {{ isEdit ? 'Actualizar Cambios' : 'Guardar y Continuar a Previsualización' }}
                </span>
              </button>
            </div>

          </FormKit>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'

const props = defineProps({
  initialData: {
    type: Object,
    default: () => ({})
  },
  actionUrl: {
    type: String,
    required: true
  },
  csrfToken: {
    type: String,
    required: true
  },
  mode: {
    type: String,
    default: 'create' // 'create' or 'edit'
  },
  initialImages: {
    type: Object,
    default: () => ({ image1: null, image2: null })
  },
  method: {
    type: String,
    default: 'POST'
  },
  rfTemplates: {
    type: Object,
    default: () => ({})
  },
  backUrl: {
    type: String,
    default: '/redes_sociales'
  }
})

const isEdit = computed(() => props.mode === 'edit')
const isSubmitting = ref(false)
const serverErrors = ref([])

// Formato y campos de datos
const formData = reactive({
  tipo_post: 'post',
  facebook: false,
  instagram: false,
  twitter: false,
  plantilla: '',
  seccion: '',
  cabecera: '',
  cabecera2: '',
  titulo: '',
  subtitulo: '',
  texto: '',
  pie: '',
  fechayhorapublicacion: '',
  video_url_1: '',
  video_url_2: '',
  publicado: false,
  ...props.initialData
})

const deleteImages = reactive({
  image1: false,
  image2: false
})

const sections = ['CONCERTADA', 'PUBLICA', 'RELIGION', 'PRIVADA', 'INFANTIL', 'LABORAL', 'INFORMACION', 'OTROS', 'EMPLEO']

const previews = reactive({
  image1: null,
  image2: null
})

const files = reactive({
  image1: null,
  image2: null
})

onMounted(() => {
  // Asegurar formato de booleanos si vienen como enteros de la BD
  formData.facebook = !!Number(formData.facebook)
  formData.instagram = !!Number(formData.instagram)
  formData.twitter = !!Number(formData.twitter)
  
  if (formData.plantilla) {
    formData.plantilla = Number(formData.plantilla)
  }

  if (props.initialData.fechayhorapublicacion) {
    try {
      const date = new Date(props.initialData.fechayhorapublicacion);
      const offset = date.getTimezoneOffset() * 60000;
      const localISOTime = (new Date(date.getTime() - offset)).toISOString().slice(0, 16);
      formData.fechayhorapublicacion = localISOTime;
    } catch (e) {
      console.error("Error al analizar la fecha:", e);
    }
  }
})

const setTipoPost = (type) => {
  formData.tipo_post = type
  
  // Limpieza de campos y requerimientos si cambiamos a Post Clásico
  if (type === 'post') {
    formData.fechayhorapublicacion = ''
    formData.pie = ''
    formData.video_url_1 = ''
    formData.video_url_2 = ''
  }
}

const onFileChange = (e, key) => {
  const file = e.target.files[0]
  if (file) {
    files[key] = file
    const reader = new FileReader()
    reader.onload = (event) => {
      previews[key] = event.target.result
    }
    reader.readAsDataURL(file)
    
    // Si había una imagen marcada para borrar, la desmarcamos
    deleteImages[key] = false
  }
}

const clearPreview = (key) => {
  previews[key] = null
  files[key] = null
}

const markImageDeleted = (key) => {
  deleteImages[key] = true
  previews[key] = null
  files[key] = null
}

const handleSubmit = async () => {
  serverErrors.value = []
  
  // Validación del lado del cliente: al menos una red social activa
  if (!formData.facebook && !formData.instagram && !formData.twitter) {
    serverErrors.value.push("Debes seleccionar al menos una red social de destino (Facebook, Instagram o Twitter).")
    window.scrollTo({ top: 0, behavior: 'smooth' })
    return
  }

  isSubmitting.value = true

  // Creación del cuerpo de petición multipart
  const fd = new FormData()
  fd.append('_token', props.csrfToken)
  if (props.method !== 'POST') {
    fd.append('_method', props.method)
  }

  // Mapeo e inserción de datos en FormData
  Object.keys(formData).forEach(key => {
    if (typeof formData[key] === 'boolean') {
      fd.append(key, formData[key] ? '1' : '0')
    } else {
      fd.append(key, formData[key] || '')
    }
  })

  // Archivos subidos
  if (files.image1) fd.append('imagen1', files.image1)
  if (files.image2) fd.append('imagen2', files.image2)

  // Marcar imágenes borradas
  if (deleteImages.image1) fd.append('delete_imagen1', '1')
  if (deleteImages.image2) fd.append('delete_imagen2', '1')

  try {
    const response = await axios.post(props.actionUrl, fd, {
      headers: { 
        'Content-Type': 'multipart/form-data',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    
    // Obtener la URL de redirección devuelta o del request
    const redirectUrl = response.request.responseURL || '/redes_sociales'
    window.location.href = redirectUrl
  } catch (error) {
    isSubmitting.value = false
    console.error(error)
    
    if (error.response && error.response.data && error.response.data.errors) {
      // Capturar los errores de validación de Laravel
      const errorsObj = error.response.data.errors
      Object.keys(errorsObj).forEach(key => {
        errorsObj[key].forEach(msg => {
          serverErrors.value.push(msg)
        })
      })
    } else if (error.response && error.response.data && error.response.data.message) {
      serverErrors.value.push(error.response.data.message)
    } else {
      serverErrors.value.push("Ocurrió un error inesperado al procesar la publicación. Inténtalo de nuevo.")
    }
    
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}
</script>

<style scoped>
/* SISTEMA DE DISEÑO Y VARIABLES */
:root {
  --brand-red: #ed1c24;
  --brand-red-dark: #cc181e;
  --fb-blue: #1877f2;
  --tw-dark: #0f1419;
}

.social-form-container {
  font-family: 'Outfit', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.modern-card {
  border-radius: 20px;
  background: #ffffff;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.03) !important;
}

.modern-card-header {
  background: #fafafb;
  border-bottom: 1px solid #f1f3f5;
}

.brand-icon-wrapper {
  background-color: #ed1c24;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.form-section-title {
  font-size: 0.95rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #1e293b;
  margin-bottom: 15px;
}

/* CARDS DE FORMATO (POST VS REEL) */
.format-card {
  background: #ffffff;
  border: 2px solid #e2e8f0;
  border-radius: 14px;
  padding: 18px 15px;
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  gap: 15px;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.format-card:hover {
  border-color: #ed1c24;
  background-color: #fffafb;
  transform: translateY(-2px);
}

.format-card.active {
  border-color: #ed1c24;
  background-color: #fff6f6;
  box-shadow: 0 8px 20px rgba(237, 28, 36, 0.06);
}

.format-icon-box {
  background: #f1f5f9;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: #ed1c24;
  transition: all 0.2s;
}

.format-card.active .format-icon-box {
  background: #fee2e2;
  color: #ed1c24;
}

.format-details {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.format-name {
  font-weight: 700;
  font-size: 0.95rem;
  color: #1e293b;
}

.format-desc {
  font-size: 0.75rem;
  color: #64748b;
  margin-top: 2px;
}

.check-indicator {
  position: absolute;
  top: 10px;
  right: 10px;
  font-size: 16px;
  color: #ed1c24;
  opacity: 0;
  transform: scale(0.7);
  transition: all 0.2s ease;
}

.format-card.active .check-indicator {
  opacity: 1;
  transform: scale(1);
}

/* BOTONES DE REDES SOCIALES (BRANDED SELECTION) */
.gap-2 {
  gap: 0.75rem;
}

.platform-btn {
  padding: 12px 24px;
  border-radius: 50px;
  border: 1.5px solid #e2e8f0;
  background: #ffffff;
  color: #64748b;
  font-weight: 600;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  outline: none;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.platform-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
}

.platform-btn.active {
  color: #ffffff !important;
}

/* Branded Active Styles */
.platform-btn.fb.active {
  background-color: #1877f2;
  border-color: #1877f2;
  box-shadow: 0 8px 20px rgba(24, 119, 242, 0.25);
}
.platform-btn.ig.active {
  background: linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%);
  border-color: transparent;
  box-shadow: 0 8px 20px rgba(220, 39, 67, 0.25);
}
.platform-btn.tw.active {
  background-color: #0f1419;
  border-color: #0f1419;
  box-shadow: 0 8px 20px rgba(15, 20, 25, 0.25);
}

.platform-icon {
  font-size: 16px;
}

/* INPUT DE SELECT NATIVO PREMIUM */
.modern-input {
  border-radius: 8px !important;
  border: 1.5px solid #cbd5e1 !important;
  padding: 10px 15px !important;
  transition: all 0.3s ease;
  height: auto !important;
}

.modern-input:focus {
  border-color: #ed1c24 !important;
  box-shadow: 0 0 0 0.25rem rgba(237, 28, 36, 0.1) !important;
}

/* MINIATURA DE PLANTILLA EN FORMULARIO */
.template-preview-box {
  background: #f8fafc;
  border-style: dashed !important;
}

.template-preview-box img {
  max-width: 160px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 10px rgba(0,0,0,0.05);
}

/* INPUT DE ARCHIVOS PREMIUM */
.premium-file-input {
  border: 1.5px dashed #cbd5e1;
  padding: 10px;
  border-radius: 8px;
  background: #f8fafc;
  transition: all 0.2s;
  cursor: pointer;
  font-size: 0.85rem;
}

.premium-file-input:hover {
  background: #f1f5f9;
  border-color: #ed1c24;
}

.preview-box-premium {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  display: inline-block;
  border-radius: 8px;
}

.btn-remove-preview {
  position: absolute;
  top: -5px;
  right: -5px;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  font-size: 10px;
  box-shadow: 0 2px 5px rgba(0,0,0,0.2);
}

.existing-img-card {
  background: #fff5f5;
  border: 1px solid #fee2e2;
}

.btn-delete-existing {
  padding: 2px 8px;
  font-size: 10px;
}

/* BOTONES DE PIE */
.btn-brand-red {
  background-color: #ed1c24 !important;
  border-color: #ed1c24 !important;
  color: #fff !important;
  border-radius: 8px;
}
.btn-brand-red:hover {
  background-color: #cc181e !important;
  border-color: #cc181e !important;
  box-shadow: 0 5px 15px rgba(237, 28, 36, 0.25) !important;
}

.btn-cancel {
  border-radius: 8px;
}

/* ALERTAS DE ERROR */
.error-alert {
  border-left: 4px solid #dc3545 !important;
  background-color: #fff5f5;
}

/* TRANSICIONES ANIMADAS */
.fade-slide-enter-active, .fade-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.fade-slide-enter-from, .fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-12px);
}
</style>
